import React, { useEffect, useRef } from 'react'
import { Editor } from '@tinymce/tinymce-react'

const EditorInput = ({
  onChange,
  s3Config,
  name,
  value,
  allowMedia = true,
  onIsUploadingMediaChanged,
  ...props
}) => {
  const editorRef = useRef(null)
  useFocusFix()

  return (
    <Editor
      apiKey="ph9ugsp39a45ewglumjhj4jsgxzg60cb2fuplk2yg1kess0u"
      ref={editorRef}
      toolbar={`bold italic bullist link${
        allowMedia ? ' insertfile image media' : ''
      } h1 emoticons`}
      value={value}
      onEditorChange={(newValue, editor) => {
        onChange?.(newValue)
      }}
      init={{
        branding: false,
        menubar: false,
        statusbar: false,
        plugins: 'lists link image media emoticons',
        file_picker_callback: function (callback, value, meta) {
          const input = document.createElement('input')
          input.setAttribute('type', 'file')

          if (meta.filetype === 'image') {
            input.setAttribute('accept', 'image/*')
          } else if (meta.filetype === 'media') {
            input.setAttribute('accept', 'video/*')
          }

          input.onchange = function () {
            const file = this.files[0]
            const reader = new FileReader()

            reader.onload = function () {
              const id = 'blobid' + new Date().getTime()
              const { blobCache } = editorRef.current.editor.editorUpload
              const base64 = reader.result.split(',')[1]
              const blobInfo = blobCache.create(id, file, base64)
              blobCache.add(blobInfo)

              callback(blobInfo.blobUri(), { title: file.name })
            }
            reader.readAsDataURL(file)
          }

          input.click()
        },
        images_upload_handler: (blobInfo, progress) =>
          uploadFileToS3(blobInfo.blob(), s3Config),
      }}
      onChange={(event, editor) => {
        // Hack to verify if a video was submitted to the editor
        if (
          event?.originalEvent?.command === 'mceInsertContent' &&
          typeof event?.originalEvent?.value === 'string' &&
          event?.originalEvent?.value?.includes('<video')
        ) {
          // Extract blob URI from video tag
          const blobUri = event.originalEvent.value
            .split('<source src="')[1]
            .split('"')[0]

          // Get blob from cache
          const blobInfo = editor.editorUpload.blobCache.getByUri(blobUri)

          // If video size is greater than 30MB, alert the user and undo the video insert
          if (blobInfo.blob().size >= 31457280) {
            alert(I18n.t('js.pro.campaigns.form.video_too_large'))
            editor.undoManager.undo()
            return
          }

          // Replace blob URI with S3 URL
          const currentTag = `<source src="data:${
            blobInfo.blob().type
          };base64,${blobInfo.base64()}">`

          const tempTag = `<source src="">`
          const tempValue = value.replace(currentTag, tempTag)
          onChange?.(tempValue)

          // Upload video to S3
          onIsUploadingMediaChanged(true)
          uploadFileToS3(blobInfo.blob(), s3Config).then(url => {
            const newTag = `<source src="${url}">`
            const newValue = value.replace(currentTag, newTag)
            onChange?.(newValue)
            onIsUploadingMediaChanged(false)
          })
        }
      }}
      textareaName={name}
      {...props}
    />
  )
}

async function uploadFileToS3(file, s3Config) {
  const body = new FormData()
  body.append('key', `${s3Config.key_start}${file.name}`)
  body.append('Content-Type', file.type)
  body.append('AWSAccessKeyId', s3Config.access_key)
  body.append('policy', s3Config.policy)
  body.append('signature', s3Config.signature)
  body.append('X-Requested-With', 'xhr')
  body.append('success_action_status', '201')
  body.append('acl', 'public-read')
  body.append('file', file)
  const res = await fetch(
    `https://s3-${s3Config.region}.amazonaws.com/${s3Config.bucket}`,
    {
      method: 'POST',
      body,
    }
  )

  const url = new DOMParser()
    .parseFromString(await res.text(), 'application/xml')
    .querySelector('Location').textContent
  return url
}

// shitty fix for tinymce modals (eg. the "add link" modal) having text inputs that cannot take focus (seems to be related to another modal existing in the page)
function useFocusFix() {
  useEffect(() => {
    const existingModal = document.querySelector('.modal')
    if (existingModal) {
      const initialTabIndex = existingModal.getAttribute('tabindex')
      existingModal.removeAttribute('tabindex')
      return () => {
        existingModal.setAttribute('tabindex', initialTabIndex)
      }
    }
  }, [])
}

export default EditorInput
